/**
 * The ONE typed analytics entry point for product events.
 *
 * Every surface calls `trackEvent(name, props)` with a name and a property
 * shape declared in `./events.ts` (the taxonomy, single source of truth), so a
 * typo or a missing property is a compile error.
 *
 * Routing: PostHog EU (consent-aware, lazy, via the `/ingest` proxy) and the
 * consent-gated Portfolio Analytics tracker. Purchases, cancellations, failed
 * payments and sign-ups are NOT tracked here: Convex records them server-side
 * from verified state changes (`convex/lib/analytics.ts`).
 *
 * Rules: never put PII (emails, names) in properties; never throw.
 */
import type { ClientAnalyticsEvents, ClientEventName } from './events'
import { captureToPostHog } from './posthog-client'
import { portfolioTrack } from './portfolio'

export type {
  AnalyticsEvents,
  AnalyticsEventName,
  ClientAnalyticsEvents,
  ClientEventName,
  ConversionSurface,
  CtaKind,
  CtaLocation,
  ServerAnalyticsEvents,
  ServerEventName,
} from './events'
export { EVENT_CATALOG, ALL_EVENT_NAMES, CTA_KINDS, CTA_LOCATIONS } from './events'

/** Optional sink override (tests). */
type Sink = (name: string, props: Record<string, unknown>) => void
let sinkOverride: Sink | null = null

export function setAnalyticsSink(sink: Sink | null) {
  sinkOverride = sink
}

export function trackEvent<TName extends ClientEventName>(
  name: TName,
  props: ClientAnalyticsEvents[TName],
): void {
  try {
    const payload = props as Record<string, unknown>
    if (sinkOverride) {
      sinkOverride(name, payload)
      return
    }
    if (typeof window === 'undefined') return
    captureToPostHog(name, payload)
    portfolioTrack(name, payload)
  } catch {
    // Analytics must never break the product.
  }
}

/** Normalise free-text search terms before they leave the browser. */
export function normalizeSearchQuery(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/gu, ' ').slice(0, 80)
}
